/**
 * Shared chat types — single source of truth for the AI assistant.
 *
 * These types are consumed by:
 *   - page.tsx (AI assistant page)
 *   - useAssistantConnection.ts (connection machine driver)
 *   - ChatUI, CallUI, MessageBlock (agent components)
 *   - HistorySidebar, ConversationHistoryDrawer (history browsing)
 *   - Zustand store + React Query hooks
 *
 * The backend Pydantic models in api/schemas.py mirror these shapes
 * so the compiler catches frontend–backend mismatches at build time.
 */

import type { RAGData } from '@/types/rag';
import type { Course } from '@/types/course';
// Type-only import (no cycle risk — support-requests.ts only depends on
// assistant-config.ts/device-token.ts, neither of which touches this file):
// SupportRequestType is the discriminant the restored
// `support_request_submitted` marker carries (see restore-messages.ts /
// handoff-timeout-submitted.tsx, which already imports the same enum).
import type { SupportRequestType } from '@/lib/api/support-requests';
// Value import (not `import type`) so the runtime `as const` objects are
// re-exported below — the WSServerMessage union also references ErrorBucket as
// a type. Generated from the backend SoT (aol_schemas.errors); see below.
import { ErrorBucket, RealtimeErrorCode } from './generated/error-contract';
// Value import (runtime `as const` objects) — generated from the shared SoT
// (aol_schemas.conversation); re-exported below under their stable names so
// consumers keep importing MessageRole/MessageType/ComponentType from `@/types/chat`.
import {
  ComponentType,
  MessageRole,
  MessageType,
} from './generated/conversation-contract';
// Value import (runtime `as const` objects) — generated from the shared SoT
// (aol_schemas.ws_events); re-exported below (mirrors the conversation-contract
// pattern above). REPLACES the hand-mirrored HandoffDomain that used to live at
// the bottom of this file — fulfills the "will be generated automatically" TODO
// (see docs/plans/2026-07-hubspot-native-handoff.md, Frontend work item 1).
// WSHandoffEventType's members are folded into WSEventType (below) rather than
// re-exported standalone, so consumers keep switching on one object.
import {
  HandoffDomain,
  HandoffEndReason,
  WSHandoffEventType,
  WSEventTag,
  ToolRetryResult,
} from './generated/ws-contract';
// Type-only import of the openapi-typescript-generated EVENT PAYLOAD SHAPES
// (as opposed to ws-contract.ts above, which only generates the wire-tag
// enums). Landed alongside the ws-dto codegen pipeline (Decision C6) so the
// handoff event/message payloads below can be derived from one generated
// source instead of hand-copied three times — see AgentAttachment,
// ContactField, ContactSummary, and the WSServerMessage/WSClientMessage
// handoff variants further down.
import type { components as WSComponents } from './generated/ws-dto';
// Value import (runtime `as const`) — the outbound-message delivery lifecycle
// owned by the connection machine; re-exported below so UI consumers keep a
// single import surface (`@/types/chat`) and don't reach into machines/.
import { SendStatus } from '@/machines/connection/types';

export { SendStatus };

// ─────────────────────────────────────────────────────────────────
// Imperative handles — refs exposed by components for parent control
// ─────────────────────────────────────────────────────────────────

/**
 * Imperative handle implemented by the conversation surface (V2's
 * `ConversationArea`). Consumed via `ChatContext.chatLayoutRef` to trigger
 * scrolls in response to WebSocket events without re-rendering the surface.
 */
export interface ChatLayoutRef {
  /** Scroll the conversation to the bottom. `duration` in ms; defaults to instant. */
  scrollToBottom: (duration?: number) => void;
}

/**
 * Imperative handle exposed by `PromptInputBar`. Consumed via
 * `ChatContext.promptInputRef` so intent actions (e.g. startNewChat) can move
 * focus into the composer without prop-drilling a DOM ref.
 */
export interface PromptInputBarRef {
  /** Focus the composer textarea. */
  focus: () => void;
}

// ─────────────────────────────────────────────────────────────────
// Enums — replace hardcoded string literals across the codebase
// Using `as const` objects for tree-shakeability and idiomatic TS
// ─────────────────────────────────────────────────────────────────

/**
 * MessageRole / MessageType / ComponentType — GENERATED from the shared single
 * source of truth (aol_schemas.conversation) into
 * `src/types/generated/conversation-contract.ts` via `./devops build types`. Do
 * NOT hand-edit the values; they can no longer drift from the backend. Re-exported
 * under the stable local names so consumers import them from `@/types/chat` as
 * before (same pattern as the error-contract + MCP re-exports).
 */
export { ComponentType, MessageRole, MessageType };

/**
 * HandoffDomain / HandoffEndReason — GENERATED from the shared single source
 * of truth (aol_schemas.ws_events) into `src/types/generated/ws-contract.ts`
 * via `./devops build types`. Do NOT hand-edit the values here; they can no
 * longer drift from the backend's live-agent-handoff contract.
 */
export { HandoffDomain, HandoffEndReason };

/**
 * ToolRetryResult — GENERATED from the shared single source of truth
 * (aol_schemas.ws_events's ToolRetryResult enum) into
 * `src/types/generated/ws-contract.ts` via `./devops build types`. Do NOT
 * hand-write these string literals — see useToolTimeoutFlow.ts's header
 * comment for the retry_id-correlation design this powers.
 */
export { ToolRetryResult };

/** MCP tool names — must match the backend tool registration in server.py.
 *  FE-only (the backend has its own constant); not part of the shared conversation
 *  contract, so this stays hand-defined here. */
export const ToolName = {
  FIND_COURSES: 'find_courses',
  BROWSE_AOL_CONTENT: 'browse_aol_content',
  FETCH_QUICK_AOL_CONTENT: 'fetch_quick_aol_content',
  // The unified retrieval tool (P3) — replaces browse + fetch_quick for LIVE
  // traffic. The two legacy names are kept for restoring old transcripts.
  SEARCH_AOL_CONTENT: 'search_aol_content',
} as const;
export type ToolName = (typeof ToolName)[keyof typeof ToolName];

/** UI modes for the AI assistant page */
export const UIState = {
  LANDING: 'landing',
  CHAT: 'chat',
  CALL: 'call',
  /** A live-agent handoff is active — the SAME conversation surface as CHAT
   *  (messages, ConversationArea) stays mounted; only the input bar's voice
   *  affordances hide and a HandoffBanner appears. See ChatContext.handoff. */
  HANDOFF: 'handoff',
} as const;
export type UIState = (typeof UIState)[keyof typeof UIState];

/** Voice visualizer animation states */
export const VizMode = {
  IDLE: 'idle',
  USER: 'user',
  AGENT: 'agent',
  THINKING: 'thinking',
} as const;
export type VizMode = (typeof VizMode)[keyof typeof VizMode];

/** WebSocket connection status */
export const ConnectionStatus = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  /** Backend is mid-reconnect to the upstream OpenAI Realtime API.
   *  Distinct from CONNECTING (initial) so the badge can show different copy. */
  RECONNECTING: 'reconnecting',
} as const;
export type ConnectionStatus =
  (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

/**
 * Error classification — GENERATED from the backend single source of truth
 * (aol_schemas.errors) into `src/types/generated/error-contract.ts` via
 * `./devops build types`. Do NOT hand-edit the values here; they can no longer
 * drift from the backend. Re-exported under the stable local names so consumers
 * import from `@/types/chat` as before (same pattern as the MCP re-exports).
 *
 * Note `ErrorBucket` now includes `BENIGN` — the browser never receives it
 * (the backend swallows benign errors), but its presence keeps this union honest
 * rather than assuming "any unknown bucket ⇒ FATAL". Drives reconnect/UX/alert
 * policy; switch on it in the WS error handler for compile-time exhaustiveness.
 */
export { ErrorBucket, RealtimeErrorCode };

// ─────────────────────────────────────────────────────────────────
// Chat message — the core data structure rendered in the UI
// ─────────────────────────────────────────────────────────────────

/**
 * A tool result attached to an assistant message.
 *
 * Discriminated on `kind` (the ComponentType) so a message carries course
 * results XOR RAG content — never both, and never a kind without its matching
 * payload. The previous shape (three independent optional fields) allowed
 * illegal states: `courses` and `ragContent` both set, or a `componentType`
 * with no payload. Those are now unrepresentable at construction.
 */
export type MessageResult =
  | {
      kind: typeof ComponentType.COURSE_BROWSER;
      courses: Course[];
      /** The location the USER searched (zip or "City, ST"), extracted from
       *  the tool-call arguments forwarded on tool_end. The course panel
       *  titles itself with this ("Part 1 courses near 07029") — the
       *  results' own postal codes can differ from what was asked. Absent
       *  on restored history (arguments aren't persisted) → the panel falls
       *  back to the first result's own location. */
      searchedLocation?: string;
    }
  | { kind: typeof ComponentType.RAG_CONTENT; ragContent: RAGData };

/**
 * A file the HubSpot rep attached to an AGENT_MESSAGE event. `url` is
 * optional by construction, not omission — HubSpot's webhook payload only
 * carries a `fileId`; the backend resolves the URL via the Files API at
 * delivery time, so it can be absent if that lookup hasn't completed or
 * failed. Rendered as a plain download link (no inline preview in v1 —
 * Decision #10, 2026-07-hubspot-native-handoff.md).
 *
 * Derived from the generated `ws-dto.d.ts` schema (Decision C6) rather than
 * hand-copied — the shape matched byte-for-byte, so this is a zero-risk
 * consolidation onto the single source of truth. Do NOT hand-edit; change
 * `aol_schemas.ws_events` and regenerate with `./devops build types`.
 */
export type AgentAttachment = WSComponents['schemas']['AgentAttachment'];

/**
 * One field the backend wants collected on the pre-handoff contact form
 * (Decision #17, 2026-07-hubspot-native-handoff.md — CONTACT_INFO_REQUESTED
 * payload). The server owns ALL policy here — which fields, required-ness,
 * prefills — the UI hardcodes none of it (see contact-info-card.tsx).
 *
 * Used to be hand-mirrored because codegen (ws-contract.ts) only emitted the
 * wire-tag string constants (WSHandoffEventType), not payload shapes. The
 * ws-dto codegen pipeline (Decision C6) now emits the full payload too, and
 * this shape matched byte-for-byte, so it's now derived rather than copied.
 */
export type ContactField = WSComponents['schemas']['ContactField'];

/**
 * Post-submit "Details shared" receipt payload (Decision #18.1,
 * 2026-07-hubspot-native-handoff.md — CONTACT_SUMMARY event / a persisted
 * SYSTEM row whose metadata carries `component: "contact_summary"`).
 *
 * Derived from the generated `ContactSummaryEvent` schema, deliberately
 * DROPPING its `type` and `handoff_id` envelope fields: `type` is the WS
 * discriminant, meaningless once this payload is attached to a `Message`
 * (which already carries its own `role`/`id`); `handoff_id` is never
 * rendered by ContactSummaryCard or read by any consumer of THIS type. The
 * one caller that does need handoff_id — useMessageManagement.ts's
 * insertContactSummary, which handles the raw live event before it's
 * flattened onto a Message (it uses handoff_id to build the dedup key) —
 * has its own ContactSummaryInsert type that extends this one with that
 * field back, rather than redeclaring name/email/phone a third time.
 * format-contact-summary.ts's ContactSummaryFields is the SAME shape as this
 * one (no narrowing at all) and just imports it directly.
 */
export type ContactSummary = Omit<
  WSComponents['schemas']['ContactSummaryEvent'],
  'type' | 'handoff_id'
>;

/**
 * Session-recovery tool-timeout offer (Decision C9/D3) — an MCP tool call
 * (find_courses / browse_aol_content) timed out and a retry is being
 * offered. camelCase mirror of the wire event's fields (see
 * ToolTimeoutOfferEvent in aol_schemas.ws_events / ws-dto.d.ts), used both
 * as ChatContext's live state (set from the TOOL_TIMEOUT_OFFER WS event) and
 * as the shape carried by a restored `Message.toolTimeoutOffer` marker — one
 * shape for both paths, same precedent as `ContactSummary` above.
 * `call_id` was removed from the wire entirely (post-review, see
 * ToolTimeoutOfferEvent's docstring in aol_schemas.ws_events) — `retryId`
 * below is what now does the correlation job it was reserved for.
 *
 * `toolLabel` was ALSO removed (copy review, governing principle: "Sara
 * speaks in the user's world, never ours" — no internal service names
 * exposed to the user). Nothing in this frontend renders it any more (see
 * tool-timeout-offer.tsx / tool-timeout-retrying.tsx / tool-timeout-
 * chip.tsx's own copy-review notes) — the eyebrow/subtext/chip that used
 * to show it went fully generic instead, since ToolTimeoutFailed already
 * did and Sara's own accompanying spoken/text apology already names the
 * specific thing that failed, in natural language, right above the card.
 * The backend is removing the field from the wire contract (and the
 * persisted marker metadata) in parallel — the backend's own
 * `_MCP_TOOL_LABELS` map is UNCHANGED and stays load-bearing there (it
 * still feeds the model's retry instructions and the persisted
 * transcript row's prose); only the WIRE field and this frontend's
 * consumption of it are gone.
 */
export interface ToolTimeoutOfferState {
  toolName: string;
  attempt: number;
  /** Backend-computed single source of truth for whether a retry should be
   *  offered at all — do NOT re-derive a cap from `attempt`. */
  retryAvailable: boolean;
  /** Server-minted id identifying THIS specific timeout incident —
   *  constant across multiple retries of the SAME offer (abandoned -> tap
   *  again -> succeeded all share one retryId); a NEW one is minted only
   *  when the tool times out again (a genuinely new incident). Echoed back
   *  on retry_tool_call and used to correlate ToolRetryOutcomeEvent to the
   *  exact incident it resolves — see useToolTimeoutFlow.ts's header
   *  comment for the full retry_id-correlation design and why the OLD
   *  turnState-based inference this replaces was unsound. */
  retryId: string;
}

/**
 * Session-recovery — the explicit outcome of ONE retry_tool_call attempt
 * (Decision C9/D3 follow-on). camelCase mirror of ToolRetryOutcomeEvent
 * (aol_schemas.ws_events / ws-dto.d.ts). LIVE-ONLY — never persisted, never
 * restored (unlike ToolTimeoutOfferState above); ChatContext holds only the
 * most recent one, and useToolTimeoutFlow correlates it against the
 * CURRENT incident's `retryId` before acting on it (a mismatched retryId —
 * a stale outcome from an incident this card has already moved past — is
 * ignored; see that hook's header comment).
 *
 * No `toolName` field (removed from the wire — see ToolRetryOutcomeEvent's
 * "No tool_name field" docstring note in aol_schemas.ws_events): a real
 * producer but no real consumer — the frontend's only reader of an
 * outcome, useToolTimeoutFlow's correlation effect, switches purely on
 * `result` and correlates purely on `retryId`; `toolName` was captured and
 * never read again. Same hazard class as the removed `toolLabel`/`call_id`
 * fields elsewhere in this file.
 */
export interface ToolRetryOutcomeState {
  retryId: string;
  result: ToolRetryResult;
}

/**
 * A single message in the chat conversation.
 *
 * Used by both the in-memory message state (page.tsx) and
 * the persisted conversation history (ConversationMessage from API).
 */
export interface Message {
  role: MessageRole;
  content?: string;
  /** Optional tool result — a course browser or RAG content card. */
  result?: MessageResult;
  /** Unique ID for deduplication and React key stability.
   *  Required — every message must have a unique ID so that:
   *  1. prev.some(m => m.id === id) dedup is safe (no undefined === undefined)
   *  2. React key={msg.id} is always stable (no index fallback)
   *  For USER messages sent through the outbox this is the client-minted
   *  uuid that also travels as metadata.client_msg_id (ack correlation +
   *  backend dedup key).
   */
  id: string;
  /** Delivery lifecycle for outbound USER messages (from the connection
   *  machine's outbox). Absent on assistant/system/restored messages and on
   *  user messages predating the outbox. queued|sent → "Sending…",
   *  failed → "Not sent · Retry", acked → no indicator. */
  status?: SendStatus;
  /** Epoch-ms timestamp of the persisted row (`created_at` from the restore
   *  payload / REST pagination). Restored messages only — live messages don't
   *  carry it (live turn timing is measured client-side instead, see
   *  ThinkingBlock). Powers the "Worked for Xs" duration on restored turns.
   *  For a restored `tool_call` row this is the tool's START time (the
   *  backend persists `created_at` at call-start, not completion) — see
   *  `completedAt` below for the matching end time. */
  createdAt?: number;
  /** Epoch-ms timestamp of when a persisted tool call COMPLETED
   *  (`metadata.call.completed_at` from the restore payload). Only set on a
   *  restored completed tool-call card — `createdAt` on that same row is the
   *  tool's START time, so using it alone for turn-duration math would
   *  undercount by the tool's runtime (see thinking-groups.ts). Live cards
   *  measure duration client-side instead (ThinkingBlock) and never set this. */
  completedAt?: number;
  /** True for messages rehydrated from conversation history (the restore
   *  mapper), false/undefined for messages produced live this session.
   *  Explicit provenance — consumed by `isAwaitingAssistantReply` so the
   *  "Sara is responding" indicators never trigger on a resumed conversation
   *  that merely happens to end on a user turn. Do NOT infer provenance from
   *  the id shape or the presence of other fields. */
  restored?: boolean;
  /** The server-side `messages.id` — a DB-minted UUID, and the ONLY id
   *  `feedback.message_id` accepts. Absent until the backend confirms this
   *  row is durably persisted:
   *    - RESTORED messages: stamped immediately by restore-messages.ts — a
   *      restored row's `id` already IS this UUID (the mapper sets it
   *      explicitly rather than leaving consumers to infer "restored ⇒ id
   *      doubles as dbId" from the `restored` flag above — one rule, no
   *      special cases: `dbId` is simply the thing you submit).
   *    - LIVE messages: populated when the backend's `message_persisted`
   *      frame arrives (useAssistantConnection.ts dispatches to
   *      useMessageManagement.ts's `setMessageDbId`) — this can lag the
   *      bubble appearing on screen, since the row isn't written until
   *      AFTER the turn completes.
   *  Deliberately a SEPARATE field from `id` (the client-visible id —
   *  a Realtime item_id or client-minted fallback — used for React keys,
   *  dedup, and the `data-message-id` scroll anchor). Conflating the two is
   *  exactly the bug this field fixes: a live thumb used to POST `id` where
   *  the backend's FK expects `messages.id` and 422'd, because only a
   *  RESTORED row's `id` happens to already be a DB UUID. See
   *  message-reaction-row.tsx, which submits `dbId`, never `id`. */
  dbId?: string;
  /** [FB36] True on a USER message whose pending turn was ABANDONED — the
   *  voice call it belonged to ended while (or before) Sara's reply was in
   *  flight, and the reply died with the call (the post-hangup rebuilt
   *  session does not resume it). Stamped by ChatContext's call-end settle
   *  (via `settleAbandonedTurn`) so `isAwaitingAssistantReply` stops
   *  counting the message — without it, a call ended mid-turn left the
   *  typing dots up forever (no message can ever land to flip the
   *  self-correcting derivation off). Distinct from `restored` on purpose:
   *  that field is provenance ("rehydrated from history"), this one is
   *  outcome ("no reply is coming"). */
  abandoned?: boolean;
  /** HUMAN_AGENT messages only — sanitized rich-text HTML (rendered via
   *  Streamdown, same pipeline as assistant content) when the rep sent
   *  formatted text. Falls back to `content` (plain text) when absent. */
  richText?: string;
  /** HUMAN_AGENT messages only — display name of the HubSpot rep who sent
   *  this message (from `senders[].name` or the actors API; see
   *  AgentMessageEvent.agent_display_name). */
  agentDisplayName?: string;
  /** HUMAN_AGENT messages only — files the rep attached. */
  attachments?: AgentAttachment[];
  /** SYSTEM messages only — the post-submit "Details shared" receipt payload
   *  (Decision #18, 2026-07-hubspot-native-handoff.md). Populated on BOTH the
   *  live CONTACT_SUMMARY event (insertContactSummary) and a restored row
   *  whose metadata.component === 'contact_summary' (restore-messages.ts).
   *  Undefined is the fallback-safety path: MessageBlock still renders
   *  `content` as a plain system notice when this is absent/malformed — see
   *  MessageBlock's system-notice branch. */
  contactSummary?: ContactSummary;
  /** SYSTEM messages only — a restored `handoff_timeout_offer` projection
   *  marker. Deliberately NOT rendered inline by message-block: page.tsx
   *  mounts the ONE HandoffTimeoutPanel at the transcript tail (see
   *  lib/handoff-timeout-restore.ts). This field exists so that derivation
   *  can find the offer; the row itself renders NOTHING (the mapper drops
   *  its `content` — the watchdog's own apology notice is the user-facing
   *  prose for this moment, and the panel carries its own lead-in chip. See
   *  restore-messages.ts's branch comment). */
  handoffTimeoutOffer?: { handoffId: string };
  /** SYSTEM messages only — a restored `tool_timeout_offer` projection
   *  marker (session-recovery, mirroring `handoffTimeoutOffer` above
   *  exactly). Deliberately NOT rendered inline by message-block: page.tsx
   *  mounts the ONE ToolTimeoutCard at the transcript tail (see
   *  lib/tool-timeout-restore.ts). This field exists so that derivation can
   *  find the offer; the row itself renders NOTHING (the mapper drops its
   *  `content` — Sara's own in-character apology is the user-facing prose
   *  for this moment, and the card carries its own copy). See
   *  restore-messages.ts's branch comment. */
  toolTimeoutOffer?: ToolTimeoutOfferState;
  /** SYSTEM messages only — a restored `support_request_submitted` marker.
   *  Renders HandoffTimeoutSubmitted in the system-notice slot, the same
   *  way `contactSummary` renders ContactSummaryCard. */
  supportRequestSubmitted?: { type: SupportRequestType; handoffId?: string };
  /** SYSTEM messages only — a restored `tool_call` row whose lifecycle never
   *  produced a renderable card: the persisted status was `running`, `failed`,
   *  or `abandoned` (see restore-messages.ts). `running` on a RESTORED page
   *  means the session died mid-tool — there is no live process left to
   *  resume, so it is display-equivalent to `abandoned`; the chip (see
   *  MessageBlock's system-notice branch) uses the same copy for all three.
   *  Live tool calls never set this — an in-progress live tool shows the
   *  Thinking timeline instead, not a system notice. */
  toolOutcome?: {
    tool: string;
    status: 'running' | 'failed' | 'abandoned';
  };
  /** True while text_delta OR (in-call) transcript_delta frames are still
   *  accumulating into this message (drives the Streamdown caret in both
   *  chat and the call transcript rail — one pipeline, no separate
   *  in-call model). Cleared when an authoritative snapshot reconciles the
   *  message (history_added/history_updated) or at turn end; the
   *  in-progress-status HISTORY_UPDATED reconcile rule in
   *  useAssistantConnection.ts is what keeps a mid-response snapshot from
   *  clearing this early — see that handler's HISTORY_UPDATED case. */
  streaming?: boolean;
  /** The OpenAI `response_id` this assistant text belongs to (from text_delta).
   *  Used to apply the backend's commentary/answer classification (msg_class). */
  responseId?: string;
  /** Commentary vs. final answer, from the backend `msg_class` signal
   *  (response-scoping: a message whose response carried a function_call is
   *  commentary → false; a message in a response with no function_call is the
   *  final answer → true). Undefined until the classification arrives — the
   *  turn grouping keeps every message in the Thinking timeline until a
   *  message is confirmed final, then pushes it out to the answer. */
  isFinal?: boolean;
}

// ─────────────────────────────────────────────────────────────────
// WebSocket protocol types
// ─────────────────────────────────────────────────────────────────

/**
 * All WebSocket event types exchanged between client and server.
 *
 * Client → Server: SETTINGS, TEXT, AUDIO
 * Server → Client: everything else
 *
 * HISTORY_RESTORED is new — sent by the server when resuming a
 * past conversation, carrying the last N decrypted messages.
 */
export const WSEventType = {
  // Client → Server
  SETTINGS: 'settings',
  TEXT: 'text',
  AUDIO: 'audio',

  // Server → Client: session lifecycle
  AGENT_START: 'agent_start',
  AGENT_END: 'agent_end',

  // Server → Client: backend-authoritative "assistant is working" signal.
  // Mirrors TURN_PHASE_* in the backend's agent/manager.py (keep in sync).
  // Replaces client-side turn-boundary inference for the typing indicator —
  // only the backend knows the true boundary (its debounced final-agent_end),
  // e.g. through the commentary→tool→generation sequence where message-shape
  // inference dropped the indicator. Old backends never send this; the UI
  // falls back to the message-shape derivation while turnState is null.
  TURN_STATE: 'turn_state',

  // Server → Client: conversation history
  HISTORY_UPDATED: 'history_updated',
  HISTORY_ADDED: 'history_added',
  HISTORY_RESTORED: 'history_restored',
  // Sent when the client's ?c= conversation_id isn't loadable for THIS device
  // (foreign / not-found). The backend mints a fresh conversation and tells the
  // client to adopt it (swap ?c=) so the URL stops pointing at someone else's
  // thread and the user's new thread is resumable. Carries the new conversation_id.
  CONVERSATION_RESET: 'conversation_reset',

  // Server → Client: streaming text/audio
  // TEXT_DELTA is the stable app-level frame the backend emits per output-text
  // token (decoupled from OpenAI's raw event name, which the GA rename moved
  // from `response.text.delta` → `response.output_text.delta`). The legacy
  // RESPONSE_TEXT_DELTA/DONE below are dormant (declared, never emitted).
  TEXT_DELTA: 'text_delta',
  // Commentary-vs-answer classification for a whole response_id (backend
  // response-scoping signal). Applied to every message with that response_id.
  MSG_CLASS: 'msg_class',
  RESPONSE_TEXT_DELTA: 'response.text.delta',
  RESPONSE_TEXT_DONE: 'response.text.done',
  TRANSCRIPT_DELTA: 'transcript_delta',
  AUDIO_TRANSCRIPT_DONE: 'response.audio_transcript.done',

  // Server → Client: audio playback
  AUDIO_DATA: 'audio',
  AUDIO_END: 'audio_end',
  AUDIO_INTERRUPTED: 'audio_interrupted',

  // Server → Client: tool invocations
  TOOL_START: 'tool_start',
  TOOL_END: 'tool_end',

  // Server → Client: tool action signals (extensible protocol)
  // Any tool can return {"action": "<name>"} and the backend emits
  // a dedicated event. Add new action types here as needed.
  /** @deprecated Legacy dual-emit partner of HANDOFF_STARTED (Decision #15,
   *  2026-07-hubspot-native-handoff.md) — the backend sends BOTH events for
   *  the same handoff during the migration window so an old cached frontend
   *  build still opens the (now-deleted) HubSpot widget as a rollback safety
   *  net. This build ignores it once HANDOFF_STARTED has been seen this
   *  session — see machines/connection/handoff-guard.ts. Remove once the
   *  backend stops dual-emitting. */
  HANDOFF_TRIGGER: 'handoff_trigger',

  // Server → Client: live-agent handoff (HubSpot Custom Channels bridge).
  // Values sourced from the generated WSHandoffEventType (aol_schemas.ws_events)
  // so this object can never drift from the backend's wire contract.
  HANDOFF_STARTED: WSHandoffEventType.HANDOFF_STARTED,
  AGENT_JOINED: WSHandoffEventType.AGENT_JOINED,
  AGENT_MESSAGE: WSHandoffEventType.AGENT_MESSAGE,
  HANDOFF_ENDED: WSHandoffEventType.HANDOFF_ENDED,
  HANDOFF_TIMEOUT: WSHandoffEventType.HANDOFF_TIMEOUT,
  /** Pre-handoff contact-collection form request (Decision #17,
   *  2026-07-hubspot-native-handoff.md) — fires BEFORE HANDOFF_STARTED while
   *  the handoff row sits in `pending_contact`. Carries the field policy
   *  (name/email/phone, required-ness, prefills) for the inline contact-info
   *  card; no HubSpot thread exists yet at this point. */
  CONTACT_INFO_REQUESTED: WSHandoffEventType.CONTACT_INFO_REQUESTED,
  /** Post-submit "Details shared" confirmation (Decision #18,
   *  2026-07-hubspot-native-handoff.md) — fires once, right after a valid
   *  CONTACT_INFO submission is accepted, deterministically BEFORE
   *  HANDOFF_STARTED (see ContactSummaryEvent's docstring in
   *  aol_schemas.ws_events). Renders as a compact read-only transcript card
   *  (contact-summary-card.tsx) in the exact spot the (now-submitted)
   *  contact form card occupied. The backend also persists an equivalent
   *  SYSTEM row (metadata.component === 'contact_summary') so the card
   *  survives reload — see restore-messages.ts. */
  CONTACT_SUMMARY: WSHandoffEventType.CONTACT_SUMMARY,

  // Client → Server: live-agent handoff replies + user-initiated end.
  AGENT_TEXT: WSHandoffEventType.AGENT_TEXT,
  END_HANDOFF: WSHandoffEventType.END_HANDOFF,
  /** Pre-handoff contact-form submission (Decision #17). One-shot control
   *  frame sent directly on the socket — same "bypasses the outbox" precedent
   *  as END_HANDOFF above: this is a form submit, not a chat message, so it
   *  has no Message bubble / SendStatus to drive through the ack-retry outbox
   *  built for text turns (see ChatContext.submitContactInfo). */
  CONTACT_INFO: WSHandoffEventType.CONTACT_INFO,

  // Server → Client: conversation metadata
  TITLE_UPDATED: 'title_updated',

  // Server → Client: delivery ack. Echoes the metadata.client_msg_id of a
  // received text frame ("received & will process"). The outbox machine
  // (src/machines/connection/) resolves the message's SendStatus on this echo;
  // a retried frame with the same id is re-acked but not re-processed
  // (backend at-most-once dedup). Old backends never send this — the machine
  // capability-gates on the first ack it sees.
  MESSAGE_RECEIVED: 'message_received',

  // Server → Client: the message-persisted id bridge. Sent once an assistant
  // turn's text row is durably written, carrying the item_id the browser
  // already keys the bubble by (Realtime item id) + the DB-minted messages.id
  // (a real UUID). Fixes per-message feedback on LIVE answers — feedback.
  // message_id is a FK to messages.id, not the Realtime item_id, so before
  // this frame existed a live thumb 422'd (only a page reload, where the
  // client id happens to already BE the DB id, ever worked). See
  // MessagePersistedEvent's docstring in aol_schemas.ws_events / ws-dto.d.ts
  // for the full rationale, and useMessageManagement.ts's setMessageDbId for
  // where the stamp lands. Value sourced from the generated WSEventTag so
  // this can never drift from the backend's wire contract.
  MESSAGE_PERSISTED: WSEventTag.MESSAGE_PERSISTED,

  // Server → Client: session-recovery — an MCP tool call (find_courses /
  // browse_aol_content) timed out and a retry is being offered — the VISUAL
  // half of C2's tool-timeout handling (see ToolTimeoutOfferEvent in
  // aol_schemas.ws_events). Not a handoff payload — value sourced from the
  // generated WSEventTag, same pattern as MESSAGE_PERSISTED above.
  TOOL_TIMEOUT_OFFER: WSEventTag.TOOL_TIMEOUT_OFFER,

  // Client → Server: the "Try again" chip on a tool_timeout_offer card
  // (session-recovery Task B). Carries `retry_id` (RetryToolCallMessage) —
  // the backend rejects the frame outright if it doesn't match the pending
  // incident (see RealtimeWebSocketManager.handle_tool_retry's docstring).
  // Value sourced from the generated WSEventTag, same pattern as
  // TOOL_TIMEOUT_OFFER above (was a hand-written literal before retry_id
  // correlation existed to generate against).
  RETRY_TOOL_CALL: WSEventTag.RETRY_TOOL_CALL,

  // Server → Client: session-recovery — the explicit outcome of ONE
  // retry_tool_call attempt (succeeded/failed/abandoned/rejected),
  // correlated by `retry_id` (see ToolRetryOutcomeEvent in
  // aol_schemas.ws_events). Added
  // to REPLACE the old turnState-busy-then-idle inference, which was
  // unsound (turnState going idle for ANY reason — a dropped socket, an
  // unrelated turn finishing, a barge-in — used to read as a false
  // success). See useToolTimeoutFlow.ts's header comment.
  TOOL_RETRY_OUTCOME: WSEventTag.TOOL_RETRY_OUTCOME,

  // Server → Client: errors
  ERROR: 'error',

  // Server → Client: in-character system message (e.g., reconnect notice,
  // out-of-quota apology). Rendered as MessageRole.SYSTEM in the chat.
  SYSTEM_MESSAGE: 'system_message',

  // Server → Client: OpenAI rate-limited this response and the backend
  // scheduled a delayed retry (retry_in seconds) — or exhausted its retry
  // budget (retry_in: null). Emitted ALONGSIDE the backend's normal
  // TURN_STATE "thinking" frame (the orb shimmer / "Working…" timeline
  // already stays alive), so this event's only job is to give the user an
  // explicit, honest reason for a multi-second silent gap instead of
  // letting it read as "broken". See useCallMode's MODEL_BUSY CallNotice
  // family for how the in-call surface renders it.
  RATE_LIMIT_WAIT: 'rate_limit_wait',

  // Client → Server: Turn 15's "Ask again" manual-retry chip (15d,
  // EXHAUSTED state) — sent when the user taps it after the backend gave up
  // retrying. FIXED wire contract shared with a parallel backend build
  // (aol-us-ai-asst); the backend re-attempts the stalled response on
  // receipt. Sent directly on the socket by useCallMode (which already owns
  // wsRef), bypassing the outbox — same one-shot-control-frame precedent as
  // END_HANDOFF/CONTACT_INFO below.
  RETRY_RESPONSE: 'retry_response',

  // Server → Client: backend is attempting to reconnect to the OpenAI
  // Realtime API. The frontend shows a transient badge; conversation
  // continues seamlessly once RECONNECTED arrives.
  RECONNECTING: 'reconnecting',
  RECONNECTED: 'reconnected',

  // Client → Server: WebRTC SDP offer, negotiated over the assistant socket
  // instead of the legacy REST POST /rtc/call — the WS frame always reaches
  // the task that owns the session, where a multi-task backend's REST POST
  // can land on a task that doesn't (see rtc-ws-negotiate.ts / rtc-call.ts's
  // negotiate()). Value sourced from the generated WSEventTag, same pattern
  // as MESSAGE_PERSISTED above.
  RTC_CALL: WSEventTag.RTC_CALL,
  // Server → Client: sent synchronously the instant rtc_call is received,
  // BEFORE any upstream work starts. Its absence within
  // RTC_WS_ACK_TIMEOUT_MS (rtc-ws-negotiate.ts) is the signal that the
  // backend predates this feature — the frontend abandons the WS path and
  // falls back to REST POST /rtc/call.
  RTC_CALL_ACK: WSEventTag.RTC_CALL_ACK,
  // Server → Client: OpenAI's SDP answer for the correlated rtc_call.
  RTC_CALL_ANSWER: WSEventTag.RTC_CALL_ANSWER,
  // Server → Client: the correlated rtc_call did not produce an answer
  // (reason: RtcCallFailureReason — upstream_error | attach_failed).
  RTC_CALL_FAILED: WSEventTag.RTC_CALL_FAILED,
  // Client → Server: WS-carried counterpart to REST POST /rtc/hangup.
  RTC_HANGUP: WSEventTag.RTC_HANGUP,
  // Client → Server: the frontend has ABANDONED the pending rtc_call
  // negotiation correlated by request_id (ack timeout, answer timeout, or
  // the socket closing outright — see rtc-ws-negotiate.ts's
  // sendCancel/rejectAllPending). Guarantees the backend never leaves a
  // call attached that this negotiation no longer owns — see
  // RtcCallCancelMessage's docstring (aol_schemas.ws_events) for the full
  // backend contract.
  RTC_CALL_CANCEL: WSEventTag.RTC_CALL_CANCEL,

  // Client → Server: the embed panel's page-context handshake — what page
  // the visitor is on, sent as a head frame (manifest only) then, if the
  // server asks, a body frame carrying just the missing sections' text (see
  // aol_schemas.ws_events's PageContextMessage docstring for the two-frame
  // protocol). Value sourced from the generated WSEventTag, same pattern as
  // MESSAGE_PERSISTED above.
  PAGE_CONTEXT: WSEventTag.PAGE_CONTEXT,
  // Server → Client: the reply to one PageContextMessage frame, correlated
  // by request_id (PageContextAckEvent). `status` says which leg of the
  // handshake this is and how it went — see page-context.ts's
  // nextPageContextAction for the decision table.
  PAGE_CONTEXT_ACK: WSEventTag.PAGE_CONTEXT_ACK,
} as const;
export type WSEventType = (typeof WSEventType)[keyof typeof WSEventType];

/**
 * Turn activity phases carried by the TURN_STATE event (backend contract —
 * TURN_PHASE_* constants in agent/manager.py).
 *
 * thinking    — a user turn opened; the model is working, no output yet
 * using_tool  — a tool is running (event carries the tool name). Deliberately
 *               spans the post-tool generation gap too: SDK text mode has no
 *               output deltas, so the next observable transition is the final
 *               text landing (responding).
 * responding  — assistant output has landed / is streaming
 * idle        — the turn is over (debounced true turn end, terminal error,
 *               or upstream reconnect)
 */
export const TurnPhase = {
  THINKING: 'thinking',
  USING_TOOL: 'using_tool',
  RESPONDING: 'responding',
  IDLE: 'idle',
} as const;
export type TurnPhase = (typeof TurnPhase)[keyof typeof TurnPhase];

/**
 * The client-held snapshot of the current turn activity (ChatContext state).
 * One object so phase+tool update atomically. `null` doubles as the
 * capability gate: an old backend never sends TURN_STATE, so turnState stays
 * null and the UI keeps the legacy message-shape derivation.
 */
export interface TurnState {
  phase: TurnPhase;
  tool: string | null;
}

/**
 * Shape of the initial settings message sent from client to server
 * when the WebSocket connection opens. Carries the device_token and the durable
 * conversation_id (client UUIDv7) for chat-history resume. `resume_conversation_id`
 * is the legacy field name the backend still accepts as a fallback.
 */
export interface WSSettingsMessage {
  type: typeof WSEventType.SETTINGS;
  device_token: string;
  conversation_id?: string | null;
  /** @deprecated use `conversation_id` — kept for older-client compatibility. */
  resume_conversation_id?: string | null;
  custom_prompt?: string;
  custom_voice?: string;
  custom_config?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────
// Server → Client: discriminated union for all WebSocket events.
// Each variant carries only the fields relevant to that event type.
// The `type` field is the discriminant — switch on it for type-safe
// access to event-specific fields.
// ─────────────────────────────────────────────────────────────────

/** Shared base fields present on every server message */
interface WSBaseMessage {
  timestamp?: number;
}

export type WSServerMessage =
  // Session lifecycle
  | (WSBaseMessage & { type: typeof WSEventType.AGENT_START })
  | (WSBaseMessage & { type: typeof WSEventType.AGENT_END })
  // Backend-authoritative turn activity. turn_id is monotonic per session —
  // the handler drops frames older than the newest seen (a late-firing idle
  // from turn N must not clear turn N+1's indicator).
  | (WSBaseMessage & {
      type: typeof WSEventType.TURN_STATE;
      phase: TurnPhase;
      tool: string | null;
      turn_id: number;
    })
  // Conversation history
  | (WSBaseMessage & {
      type: typeof WSEventType.HISTORY_UPDATED;
      history: unknown[];
      conversation_id?: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.HISTORY_ADDED;
      item: unknown;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.HISTORY_RESTORED;
      messages: unknown[];
      conversation_id?: string;
      /** Stored title of the resumed conversation (may be null if never
       *  generated). Lets a ?c=/reload restore populate the header — the only
       *  other title source, TITLE_UPDATED, fires on generation, not replay. */
      title?: string | null;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.CONVERSATION_RESET;
      conversation_id: string;
      reason?: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.MESSAGE_RECEIVED;
      client_msg_id: string;
    })
  // Message-persisted id bridge (see WSEventType.MESSAGE_PERSISTED above for
  // the full rationale). Derived from the generated MessagePersistedEvent
  // rather than hand-copied — same pattern as the handoff variants below.
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['MessagePersistedEvent'], 'type'> & {
        type: typeof WSEventType.MESSAGE_PERSISTED;
      })
  // Session-recovery tool-timeout offer (see WSEventType.TOOL_TIMEOUT_OFFER
  // above). Derived from the generated ToolTimeoutOfferEvent — same pattern
  // as MessagePersistedEvent above. Now carries `retry_id` automatically
  // (the generated DTO grew the field; nothing to change here).
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['ToolTimeoutOfferEvent'], 'type'> & {
        type: typeof WSEventType.TOOL_TIMEOUT_OFFER;
      })
  // Session-recovery — the explicit outcome of ONE retry_tool_call attempt
  // (see WSEventType.TOOL_RETRY_OUTCOME above). Derived from the generated
  // ToolRetryOutcomeEvent — same pattern as ToolTimeoutOfferEvent above.
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['ToolRetryOutcomeEvent'], 'type'> & {
        type: typeof WSEventType.TOOL_RETRY_OUTCOME;
      })
  // Streaming text — token-by-token frame accumulated by item_id
  | (WSBaseMessage & {
      type: typeof WSEventType.TEXT_DELTA;
      delta: string;
      item_id?: string;
      content_index?: number;
      response_id?: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.MSG_CLASS;
      response_id: string;
      is_final: boolean;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.RESPONSE_TEXT_DELTA;
      delta: string;
      item_id?: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.RESPONSE_TEXT_DONE;
      text?: string;
      item_id?: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.TRANSCRIPT_DELTA;
      transcript: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.AUDIO_TRANSCRIPT_DONE;
      transcript?: string;
    })
  // Audio playback
  | (WSBaseMessage & {
      type: typeof WSEventType.AUDIO_DATA;
      audio: string;
    })
  | (WSBaseMessage & { type: typeof WSEventType.AUDIO_END })
  | (WSBaseMessage & { type: typeof WSEventType.AUDIO_INTERRUPTED })
  // Tool invocations
  | (WSBaseMessage & {
      type: typeof WSEventType.TOOL_START;
      tool: string;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.TOOL_END;
      tool: string;
      output?: string;
      status?: string;
      error?: string;
      /** Tool-call arguments as the model issued them (e.g. find_courses'
       *  zip_code/city/state) — forwarded by the backend so renderers can
       *  reflect what was SEARCHED, not just what came back. */
      arguments?: Record<string, unknown>;
    })
  // Tool action protocol
  /** @deprecated see the HANDOFF_TRIGGER JSDoc in the WSEventType object above. */
  | (WSBaseMessage & {
      type: typeof WSEventType.HANDOFF_TRIGGER;
      domain: HandoffDomain;
    })
  // Live-agent handoff (HubSpot Custom Channels bridge) — server → client.
  // Dual-emitted alongside the legacy HANDOFF_TRIGGER above during the
  // migration window (Decision #15, 2026-07-hubspot-native-handoff.md).
  // Pre-handoff contact-collection form request (Decision #17). Precedes
  // HANDOFF_STARTED: the handoff row is in `pending_contact` and no HubSpot
  // thread exists yet. Repeated for the same handoff_id is a valid,
  // server-driven correction loop (invalid submit, or a page reload while
  // still pending) — the client always replaces its fields/prefills with the
  // latest event (see buildCollectingHandoff in useHandoff.ts).
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['ContactInfoRequestedEvent'], 'type'> & {
        type: typeof WSEventType.CONTACT_INFO_REQUESTED;
      })
  // Post-submit "Details shared" confirmation (Decision #18). Fires once,
  // between a valid CONTACT_INFO submission and HANDOFF_STARTED.
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['ContactSummaryEvent'], 'type'> & {
        type: typeof WSEventType.CONTACT_SUMMARY;
      })
  | (WSBaseMessage &
      Omit<
        WSComponents['schemas']['HandoffStartedEvent'],
        'type' | 'reason'
      > & {
        type: typeof WSEventType.HANDOFF_STARTED;
        // Required here, unlike the generated schema's optional-with-a-
        // default-"" `reason?`: that optionality describes what a REQUEST
        // body may omit, not what a live event omits — the backend always
        // populates `reason` (possibly "") when it constructs and emits
        // this event, so every real HANDOFF_STARTED frame has it. Flagged
        // during the C6 ws-dto consolidation as a hand-written/generated
        // discrepancy worth preserving, not "fixing" — loosening this would
        // force a null-check on every consumer for a case that can't occur.
        reason: string;
      })
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['AgentJoinedEvent'], 'type'> & {
        type: typeof WSEventType.AGENT_JOINED;
      })
  // `message_id` (from HubSpot message.id) is the frontend's dedup key — a
  // webhook redelivery can resend the same message; see
  // insertHumanAgentMessage.
  | (WSBaseMessage &
      Omit<
        WSComponents['schemas']['AgentMessageEvent'],
        'type' | 'attachments'
      > & {
        type: typeof WSEventType.AGENT_MESSAGE;
        // Required here — same "has-a-default ≠ absent on a live event" case
        // as HANDOFF_STARTED.reason above; the backend always sends
        // `attachments` (as `[]` when there are none).
        attachments: AgentAttachment[];
      })
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['HandoffEndedEvent'], 'type'> & {
        type: typeof WSEventType.HANDOFF_ENDED;
      })
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['HandoffTimeoutEvent'], 'type'> & {
        type: typeof WSEventType.HANDOFF_TIMEOUT;
      })
  // Errors — bucket-aware after the 2026-05 refactor. The `error: string`
  // field is gone (clean cutover). Every ERROR event carries the typed
  // bucket/code/user_message contract. Unknown buckets fall back to FATAL
  // server-side, so the union is closed.
  | (WSBaseMessage & {
      type: typeof WSEventType.ERROR;
      bucket: ErrorBucket;
      code: string;
      user_message: string;
      retryable: boolean;
    })
  // In-character system message (Sara-voice copy). The conversation
  // pipeline appends a Message with role=SYSTEM when this arrives.
  | (WSBaseMessage & {
      type: typeof WSEventType.SYSTEM_MESSAGE;
      content: string;
      bucket?: ErrorBucket;
      code?: string;
    })
  // OpenAI rate-limited this response; a delayed retry is scheduled
  // (retry_in seconds) or the retry budget is exhausted (retry_in: null).
  // `attempt` is the 1-based retry attempt number — not currently rendered
  // (Turn 15's patience notice is a deliberately INDETERMINATE sweep, not a
  // countdown/attempt UI — see lib/call-notice.ts's patienceNotice()), but
  // kept typed for parity with the backend frame and future use.
  | (WSBaseMessage & {
      type: typeof WSEventType.RATE_LIMIT_WAIT;
      retry_in: number | null;
      attempt: number;
    })
  // Reconnect lifecycle — drives ConnectionStatus.RECONNECTING badge
  | (WSBaseMessage & {
      type: typeof WSEventType.RECONNECTING;
      attempt: number;
    })
  | (WSBaseMessage & {
      type: typeof WSEventType.RECONNECTED;
    })
  // WebRTC SDP-over-WS transport frames (voice-call migration off REST
  // POST /rtc/call — see rtc-ws-negotiate.ts). These are pure transport:
  // consumed directly by the pending-request registry in
  // rtc-ws-negotiate.ts via handleRtcWsEvent, never touch ChatContext or
  // the reducer (see the RTC_CALL_ACK/ANSWER/FAILED cases in
  // useAssistantConnection.ts, which just forward-and-break).
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['RtcCallAckEvent'], 'type'> & {
        type: typeof WSEventType.RTC_CALL_ACK;
      })
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['RtcCallAnswerEvent'], 'type'> & {
        type: typeof WSEventType.RTC_CALL_ANSWER;
      })
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['RtcCallFailedEvent'], 'type'> & {
        type: typeof WSEventType.RTC_CALL_FAILED;
      })
  // Page-context handshake ack (see PAGE_CONTEXT/PAGE_CONTEXT_ACK JSDoc
  // above) — consumed by the page-context decision machine
  // (machines/connection/page-context.ts), not by ChatContext.
  | (WSBaseMessage &
      Omit<WSComponents['schemas']['PageContextAckEvent'], 'type'> & {
        type: typeof WSEventType.PAGE_CONTEXT_ACK;
      })
  // Client-synthesized variant for events not in the union above. A parser
  // maps any unrecognized server `type` to this shape so the union stays
  // CLOSED: a `{ type: string }` member would widen the discriminant to
  // `string`, collapsing narrowing for EVERY variant (a switch on
  // `msg.type` would no longer narrow `msg.bucket`, `msg.delta`, etc.).
  // `raw` preserves the original payload for diagnostics/logging.
  | (WSBaseMessage & {
      type: 'unknown';
      raw: unknown;
    });

// ─────────────────────────────────────────────────────────────────
// Client → Server: discriminated union for outgoing messages.
// Replaces the `sendMessage: (message: any)` signature.
// ─────────────────────────────────────────────────────────────────

export type WSClientMessage =
  | WSSettingsMessage
  | {
      type: typeof WSEventType.TEXT;
      text: string;
      /** client_generated marks optimistic sends; client_msg_id is the
       *  outbox's ack-correlation + backend dedup key (see MESSAGE_RECEIVED). */
      metadata?: { client_generated?: boolean; client_msg_id?: string };
    }
  | { type: typeof WSEventType.AUDIO; audio: string }
  // Turn 15's "Ask again" chip (15d) — see the RETRY_RESPONSE JSDoc above.
  // No payload: the backend correlates the retry against the session's
  // current stalled turn, not an explicit id.
  | { type: typeof WSEventType.RETRY_RESPONSE }
  // Session-recovery "Try again" chip on a tool_timeout_offer card — see the
  // RETRY_TOOL_CALL JSDoc above. Sent directly on the socket, bypassing the
  // outbox (see ChatContext.retryToolCall), same one-shot-control-frame
  // precedent as END_HANDOFF/CONTACT_INFO below. `retry_id` MUST echo the
  // exact value the triggering ToolTimeoutOfferEvent carried — the backend
  // rejects the frame outright otherwise (see RetryToolCallMessage's
  // docstring in aol_schemas.ws_events).
  | { type: typeof WSEventType.RETRY_TOOL_CALL; retry_id: string }
  // Live-agent handoff — client → server. AGENT_TEXT reuses the outbox/ack
  // machinery (see machines/connection/send-routing.ts); END_HANDOFF is a
  // one-shot control frame sent directly on the socket (no ack/retry needed —
  // like audio, it bypasses the outbox by design).
  | (Omit<
      WSComponents['schemas']['AgentTextMessage'],
      'type' | 'client_msg_id'
    > & {
      type: typeof WSEventType.AGENT_TEXT;
      // Narrower than the generated `string | null` — this codebase only
      // ever OMITS client_msg_id, never sends an explicit null (see the
      // outbox's ack-correlation path in machines/connection/). The
      // generated `| null` reflects what Pydantic's Optional[str] accepts
      // from any client, not what this one produces.
      client_msg_id?: string;
    })
  | (Omit<WSComponents['schemas']['EndHandoffMessage'], 'type'> & {
      type: typeof WSEventType.END_HANDOFF;
    })
  // Pre-handoff contact-form submission (Decision #17). Like END_HANDOFF,
  // sent directly on the socket (see ChatContext.submitContactInfo) — no
  // ack/retry lifecycle: the response IS either HANDOFF_STARTED (accepted)
  // or a fresh CONTACT_INFO_REQUESTED (server-side validation failed).
  | (Omit<WSComponents['schemas']['ContactInfoMessage'], 'type' | 'phone'> & {
      type: typeof WSEventType.CONTACT_INFO;
      // Narrower than generated `string | null` — same rationale as
      // AGENT_TEXT's client_msg_id above; this form only ever omits phone.
      phone?: string;
    })
  // WebRTC SDP offer / hangup — sent directly on the socket by
  // rtc-ws-negotiate.ts / rtc-call.ts (wsSend), bypassing the outbox: same
  // one-shot-control-frame precedent as END_HANDOFF/CONTACT_INFO above.
  | (Omit<WSComponents['schemas']['RtcCallMessage'], 'type'> & {
      type: typeof WSEventType.RTC_CALL;
    })
  | (Omit<WSComponents['schemas']['RtcHangupMessage'], 'type'> & {
      type: typeof WSEventType.RTC_HANGUP;
    })
  // Abandons a pending rtc_call negotiation — sent by rtc-ws-negotiate.ts's
  // sendCancel/rejectAllPending, same one-shot-control-frame precedent as
  // RTC_CALL/RTC_HANGUP above.
  | (Omit<WSComponents['schemas']['RtcCallCancelMessage'], 'type'> & {
      type: typeof WSEventType.RTC_CALL_CANCEL;
    })
  // Page-context head/body frame — sent directly on the socket by
  // useAssistantConnection's ws.onopen handshake driver
  // (machines/connection/page-context.ts), bypassing the outbox: same
  // one-shot-control-frame precedent as RTC_CALL/END_HANDOFF above.
  | (Omit<WSComponents['schemas']['PageContextMessage'], 'type'> & {
      type: typeof WSEventType.PAGE_CONTEXT;
    });

// ─────────────────────────────────────────────────────────────────
// Handoff state — ChatContext's live-agent handoff context
// (composed via useHandoff; see src/hooks/useHandoff.ts).
// HandoffDomain/HandoffEndReason are generated — see the re-export
// near the top of this file.
// ─────────────────────────────────────────────────────────────────

/**
 * Phase of an active live-agent handoff.
 *   collecting — pre-handoff contact-form gate (Decision #17,
 *               2026-07-hubspot-native-handoff.md): CONTACT_INFO_REQUESTED
 *               fired; no HubSpot thread exists yet. uiState deliberately
 *               STAYS CHAT during this phase (see the CONTACT_INFO_REQUESTED
 *               case in useAssistantConnection.ts) — Sara/the input bar stay
 *               live while the inline contact-info-card.tsx renders. Domain
 *               is unknown until HANDOFF_STARTED (see HandoffContext.domain).
 *   waiting   — handoff started; no HubSpot rep has joined yet.
 *   connected — a rep joined (AGENT_JOINED), OR the first AGENT_MESSAGE
 *               arrived before the join signal did — the join webhook
 *               (conversation.propertyChange:assignedTo) is best-effort, so a
 *               message landing first is proof enough a human is live and
 *               auto-promotes the phase (see useHandoff.agentMessageReceived).
 *   ended     — resolved (agent_resolved) or user-ended (user_ended). See
 *               `HandoffContext.endReason`. UIState may not have flipped back
 *               to CHAT in the same tick as this update.
 *   timed_out — HANDOFF_TIMEOUT: no rep ever joined before the backend's
 *               no-agent timeout fired. Kept as its OWN phase rather than
 *               folded into `ended` (which is how this used to work) because
 *               "nobody was available" and "a human resolved/ended this" call
 *               for different recovery UI (see presence-ribbon.tsx's timeout
 *               variant) — collapsing them loses the ability to offer a
 *               retry/alternate path from the timeout case specifically.
 */
export const HandoffPhase = {
  COLLECTING: 'collecting',
  WAITING: 'waiting',
  CONNECTED: 'connected',
  ENDED: 'ended',
  TIMED_OUT: 'timed_out',
} as const;
export type HandoffPhase = (typeof HandoffPhase)[keyof typeof HandoffPhase];

/**
 * In-call notification family (Turn-12 mic-management design, 12b): one
 * shape, four temperatures — amber asks, green reassures, red blocks, and
 * (Turn 15, 15b) a warm PATIENCE that just waits. Rendered by
 * voice/call-notice.tsx INSIDE the call surface; produced by useCallMode.
 * Pre-call startup failures (mic permission, no mic) stay on sonner toasts
 * because the call screen isn't mounted yet to host these.
 */
export const CallNoticeKind = {
  WARNING: 'warning',
  SUCCESS: 'success',
  ERROR: 'error',
  /** Turn 15 (15b) — the calm WAITING treatment for a scheduled rate-limit
   *  retry (rate_limit_wait, retry_in non-null). Deliberately its own
   *  temperature, not WARNING: amber asks the user for something (switch
   *  mics, fix a setting); PATIENCE is a wait with nothing for the user to
   *  do, so it carries no icon, no action, and no dismiss (see
   *  lib/call-notice.ts's patienceNotice()). EXHAUSTED (retry_in: null)
   *  stays on WARNING, not ERROR — see exhaustedNotice()'s doc comment for
   *  why red is reserved for true failures. */
  PATIENCE: 'patience',
} as const;
export type CallNoticeKind =
  (typeof CallNoticeKind)[keyof typeof CallNoticeKind];

/**
 * What a notice is ABOUT — the retraction rules key on this, not on kind.
 * A speech-level peak retracts MIC_SILENCE warnings ("your mic hears
 * nothing" — speech falsifies the claim) but must NOT retract a CONNECTION
 * warning: talking while the socket is down proves nothing about the
 * network. MODEL_BUSY (rate_limit_wait) is retracted by its own signal too —
 * the backend reporting agent output has started (turn_state: responding) —
 * not by speech or a connection restore, which say nothing about whether
 * OpenAI is still rate-limiting responses. See lib/call-notice.ts.
 */
export const CallNoticeTopic = {
  MIC_SILENCE: 'micSilence',
  CONNECTION: 'connection',
  MODEL_BUSY: 'modelBusy',
} as const;
export type CallNoticeTopic =
  (typeof CallNoticeTopic)[keyof typeof CallNoticeTopic];

export interface CallNoticeAction {
  label: string;
  onClick: () => void;
}

/** One in-call notice. SUCCESS auto-dismisses (~3.4s, matching the design's
 *  dismissBar hairline); WARNING/ERROR persist until dismissed/superseded.
 *  PATIENCE (Turn 15) also persists but is NEVER dismissable — no ×, no
 *  swipe; it's cleared only by useCallMode's own model-busy retraction. */
export interface CallNotice {
  kind: CallNoticeKind;
  topic: CallNoticeTopic;
  title: string;
  subtitle?: string;
  action?: CallNoticeAction;
}

/** Live-agent handoff state held in ChatContext. Null when no handoff is
 *  active (or none has happened yet this session). */
export interface HandoffContext {
  handoffId: string;
  /** Undefined only during COLLECTING — CONTACT_INFO_REQUESTED (the event
   *  that starts that phase) precedes HANDOFF_STARTED, which is the first
   *  event that actually carries a domain (Decision #17). Always present
   *  from WAITING onward. */
  domain?: HandoffDomain;
  phase: HandoffPhase;
  agentDisplayName?: string;
  /** Present only during COLLECTING — the server-driven field list to render
   *  in the inline pre-handoff contact-info card (Decision #17). The server
   *  owns all policy (which fields, required-ness, prefills); the UI
   *  hardcodes none of it. */
  fields?: ContactField[];
  /** Present only from HANDOFF_ENDED (phase ENDED) — the server's reason code
   *  for why the handoff closed. Absent for TIMED_OUT: HANDOFF_TIMEOUT
   *  carries no end_reason field on the wire (see WSServerMessage), so this
   *  is never synthesized/guessed for that phase. */
  endReason?: HandoffEndReason;
}
